import React from "react";
import { ArrowUpRight, ShieldCheck } from "lucide-react";
import PlaceholderImage from "@/components/PlaceholderImage";
import SectionReveal from "@/components/SectionReveal";
import { LOGOS, PAGE_BANNERS } from "@/data/assets";

/* Reusable partner logo tile with real image */
function LogoTile({ label, name, src, testId, tone = "light" }) {
  const dark = tone === "dark";
  return (
    <div
      className={`border p-6 lg:p-8 ${dark ? "bg-black border-black" : "bg-white border-black/10"}`}
      data-testid={testId}
    >
      <div
        className={`w-full aspect-[4/1] flex items-center justify-center ${dark ? "bg-black" : "bg-white"}`}
        data-placeholder-label={label}
      >
        <img
          src={src}
          alt={name}
          className="max-h-full max-w-full object-contain"
        />
      </div>
      <div
        className={`mt-4 font-heading uppercase tracking-[0.14em] text-sm font-semibold text-center ${
          dark ? "text-white" : "text-black"
        }`}
      >
        {name}
      </div>
    </div>
  );
}

export default function Authenticity() {
  return (
    <>
      {/* AUTHENTICITY BANNER */}
      <section data-testid="authenticity-hero" className="bg-white">
        <div className="relative">
          <PlaceholderImage
            label="Authenticity Banner"
            ratio="aspect-[21/9]"
            tone="dark"
            testId="authenticity-banner"
            src={PAGE_BANNERS.authenticity}
            alt="Authenticity — MusclePharm India"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/45 to-transparent" />
          <div className="absolute inset-0 mp-container flex items-center">
            <div className="text-left w-full max-w-4xl">
              <div
                className="font-heading uppercase tracking-[0.32em] text-[11px] mb-4 font-semibold"
                style={{ color: "var(--mp-green)" }}
              >
                Authenticity
              </div>
              <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl uppercase leading-[0.9] text-white">
                Every unit,<br />
                verifiable.
              </h1>
              <p className="mt-6 max-w-xl font-body text-white/90 text-lg leading-relaxed">
                MusclePharm India is committed to protecting Indian consumers from counterfeit product. Every genuine unit carries a TrueMPN authenticity sticker.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      <section data-testid="authenticity-partners" className="bg-white py-20 lg:py-24">
        <div className="mp-container grid grid-cols-1 lg:grid-cols-12 gap-10">
          <SectionReveal className="lg:col-span-6">
            <div className="mp-eyebrow-green">Exclusively Brought To India By</div>
            <div className="mt-4">
              <LogoTile
                label="Muscle Pro Nutrition Logo"
                name="MusclePro Nutrition"
                src={LOGOS.musclePro}
                testId="authenticity-mpn-logo"
                tone="dark"
              />
            </div>
            <p className="mt-4 font-body text-black/75 leading-relaxed text-sm text-center">
              MusclePro Nutrition Private Limited — exclusive licensed partner of MusclePharm in India.
            </p>
            <div className="mt-6 flex justify-center">
              <a
                href="https://musclepronutrition.com/authenticate/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="authenticity-partners-truempn"
                className="mp-btn mp-btn-primary"
              >
                Verify On TrueMPN <ArrowUpRight size={16} />
              </a>
            </div>
          </SectionReveal>

          <SectionReveal delay={120} className="lg:col-span-6">
            <div className="mp-eyebrow-green">Distributed Across India By</div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <LogoTile
                label="Nutriride Logo"
                name="Nutriride"
                src={LOGOS.nutriride}
                testId="authenticity-distributor-nutriride"
              />
              <LogoTile
                label="ADS Gallery Logo"
                name="ADS Gallery"
                src={LOGOS.adsGallery}
                testId="authenticity-distributor-ads"
              />
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* BODY COPY + STEPS */}
      <section
        data-testid="authenticity-body"
        className="bg-[color:var(--mp-off-white)] py-20 lg:py-24"
      >
        <div className="mp-container grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <SectionReveal className="lg:col-span-7">
            <div className="mp-eyebrow-green">100% Genuine</div>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95]">
              Guaranteed authentic.<br />
              Verifiable in seconds.
            </h2>
            <p className="mt-8 font-body text-black/80 leading-relaxed text-lg">
              MusclePro Nutrition Private Limited is a leading distributor of sports nutrition in India, and the exclusive licensed partner of MusclePharm for the Indian market. With a long history of shaping the Indian sports nutrition industry, we exist to give Indian customers access to premium, honest, high-quality sports nutrition — through the right channels, without compromise.
            </p>
            <p className="mt-4 font-body text-black/80 leading-relaxed text-lg">
              Every product sold in India through our authorised sellers is guaranteed 100% genuine and can be independently verified on the TrueMPN app in seconds. If your unit does not carry a TrueMPN sticker, or if verification fails — it is not a genuine MusclePharm India product.
            </p>
            <div className="mt-8">
              <a
                href="https://musclepronutrition.com/authenticate/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="authenticity-truempn-cta"
                className="mp-btn mp-btn-primary"
              >
                Verify On TrueMPN <ArrowUpRight size={16} />
              </a>
            </div>
          </SectionReveal>

          <SectionReveal delay={120} className="lg:col-span-5">
            <div className="border border-black/10 bg-white p-8">
              <div
                className="w-14 h-14 flex items-center justify-center border"
                style={{ borderColor: "var(--mp-green)", color: "var(--mp-green-hover)" }}
              >
                <ShieldCheck size={26} />
              </div>
              <h3 className="mt-6 font-display text-2xl uppercase leading-none">
                How To Verify
              </h3>
              <ol className="mt-6 space-y-4 font-body text-black/80 leading-relaxed">
                <li className="flex gap-3">
                  <span
                    className="font-display text-2xl leading-none w-6 shrink-0"
                    style={{ color: "var(--mp-green-hover)" }}
                  >
                    1
                  </span>
                  Look for the TrueMPN authenticity sticker on your MusclePharm India unit.
                </li>
                <li className="flex gap-3">
                  <span
                    className="font-display text-2xl leading-none w-6 shrink-0"
                    style={{ color: "var(--mp-green-hover)" }}
                  >
                    2
                  </span>
                  Open the TrueMPN authentication page using the button above.
                </li>
                <li className="flex gap-3">
                  <span
                    className="font-display text-2xl leading-none w-6 shrink-0"
                    style={{ color: "var(--mp-green-hover)" }}
                  >
                    3
                  </span>
                  Enter or scan the code from your sticker — you will receive an instant genuine-verification.
                </li>
              </ol>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* ORDERING & RETURNS */}
      <section
        data-testid="authenticity-faq"
        className="bg-white py-20 lg:py-24 border-t border-black/10"
      >
        <div className="mp-container grid grid-cols-1 lg:grid-cols-12 gap-10">
          <SectionReveal className="lg:col-span-4">
            <div className="mp-eyebrow-green">FAQ · Returns</div>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl uppercase leading-[0.95]">
              Ordering & Returns
            </h2>
          </SectionReveal>
          <div className="lg:col-span-8 space-y-5">
            <div className="border border-black/10 bg-[color:var(--mp-off-white)] p-6 lg:p-8">
              <div
                className="font-heading uppercase tracking-[0.2em] text-sm font-semibold"
                style={{ color: "var(--mp-green-hover)" }}
              >
                Ordering
              </div>
              <p className="mt-3 font-body text-black/80 leading-relaxed">
                MusclePharm India products will be available exclusively on Amazon India through our authorised sellers — <strong>Nutriride</strong> and <strong>ADS Gallery</strong>. Always ensure every unit you receive carries a TrueMPN authenticity sticker before opening. If you are unsure whether a listing is authorised, contact us at{" "}
                <a href="mailto:info@musclepronutrition.com" className="text-black hover:text-[color:var(--mp-green-hover)] underline underline-offset-4 font-semibold">
                  info@musclepronutrition.com
                </a>{" "}
                before purchase.
              </p>
            </div>
            <div className="border border-black/10 bg-[color:var(--mp-off-white)] p-6 lg:p-8">
              <div
                className="font-heading uppercase tracking-[0.2em] text-sm font-semibold"
                style={{ color: "var(--mp-green-hover)" }}
              >
                Returns
              </div>
              <p className="mt-3 font-body text-black/80 leading-relaxed">
                Returns for orders placed on Amazon India are handled by the seller according to Amazon&apos;s policies. For product-related queries, damage-in-transit reports or return escalations, please contact{" "}
                <a
                  href="mailto:info@adsgallery.com"
                  className="text-black hover:text-[color:var(--mp-green-hover)] underline underline-offset-4 font-semibold"
                >
                  info@adsgallery.com
                </a>{" "}
                or{" "}
                <a
                  href="mailto:info@musclepronutrition.com"
                  className="text-black hover:text-[color:var(--mp-green-hover)] underline underline-offset-4 font-semibold"
                >
                  info@musclepronutrition.com
                </a>
                .
              </p>
            </div>
            <div className="border border-black/10 bg-[color:var(--mp-off-white)] p-6 lg:p-8">
              <div
                className="font-heading uppercase tracking-[0.2em] text-sm font-semibold"
                style={{ color: "var(--mp-green-hover)" }}
              >
                Counterfeit Report
              </div>
              <p className="mt-3 font-body text-black/80 leading-relaxed">
                If you have received a MusclePharm-labelled product in India that does not verify on the TrueMPN app, please report it to{" "}
                <a
                  href="mailto:info@musclepronutrition.com"
                  className="text-black hover:text-[color:var(--mp-green-hover)] underline underline-offset-4 font-semibold"
                >
                  info@musclepronutrition.com
                </a>
                {" "}with the seller details and photographs of the unit. Protecting genuine athletes from counterfeit product is a priority for MusclePharm India.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
